import { FEATURES, LIMITS, PLANS, type Plan } from "@sprout/shared/plans";

export { FEATURES, LIMITS, PLANS };
export type LimitId = keyof Plan["limits"];

export const FREE = PLANS[0]!;
export const PAID = PLANS.find((p) => p.prices.length) ?? PLANS[PLANS.length - 1]!;

const money = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: amount % 100 ? 2 : 0,
  }).format(amount / 100);

/** "$4.99" + "/mo" + "or $49 a year", or "$0" for a free plan. */
export function planPrice(plan: Plan) {
  const monthly = plan.prices.find((p) => p.interval === "month");
  const annual = plan.prices.find((p) => p.interval === "year");
  if (!monthly && !annual) return { amount: money(0, "usd"), per: "", note: "Always free for families" };
  const main = monthly ?? annual!;
  return {
    amount: money(main.amount, main.currency),
    per: monthly ? "/mo" : "/yr",
    note: monthly && annual ? `or ${money(annual.amount, annual.currency)} a year` : "",
  };
}

const mb = (value: number) => (value >= 1024 ? `${value / 1024} GB` : `${value} MB`);

/** Short form for stat tiles ("30 sec"), long form for the comparison table ("30 seconds"). */
export function limitValue(id: LimitId, value: number, short = false) {
  if (id === "proofStorageMb" || id === "clipStorageMb") return mb(value);
  if (id === "proofRetentionMaxDays") return `${value} days`;
  if (id === "maxVideoSeconds") return short ? `${value} sec` : `${value} seconds`;
  return String(value);
}

export const limitName = (id: LimitId) => LIMITS.find((l) => l.id === id)?.name ?? id;
export const featureFor = (id: string) => FEATURES.find((f) => f.id === id);

/** Features a paid plan adds on top of the free one. */
export const plusFeatures = (plan: Plan) =>
  plan.features.filter((f) => !FREE.features.includes(f)).map(featureFor).filter((f) => !!f);
